import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Plus, Receipt, Fuel, Coffee, Bed, Wrench, 
  ParkingCircle, Scale, MoreHorizontal, Loader2, Trash2,
  DollarSign, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const EXPENSE_CATEGORIES = [
  { value: 'fuel', label: 'Fuel', icon: Fuel, color: 'text-orange-400' },
  { value: 'tolls', label: 'Tolls', icon: DollarSign, color: 'text-blue-400' },
  { value: 'meals', label: 'Meals', icon: Coffee, color: 'text-yellow-400' },
  { value: 'lodging', label: 'Lodging', icon: Bed, color: 'text-purple-400' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'text-red-400' },
  { value: 'parking', label: 'Parking', icon: ParkingCircle, color: 'text-cyan-400' },
  { value: 'scales', label: 'Scales', icon: Scale, color: 'text-green-400' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'text-gray-400' },
];

const DriverExpensesPage = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    category: 'fuel',
    amount: '',
    description: '',
    location: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expensesRes, summaryRes] = await Promise.all([
        api.get('/driver/expenses'),
        api.get('/driver/expenses/summary')
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/driver/login');
      } else {
        toast.error('Failed to load expenses');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      // Get current location
      let latitude = null;
      let longitude = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (e) {
          // Location not available, continue without it
        }
      }

      await api.post('/driver/expenses', {
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        location: formData.location || null,
        latitude,
        longitude
      });

      toast.success('Expense recorded');
      setShowAddDialog(false);
      setFormData({ category: 'fuel', amount: '', description: '', location: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Delete this expense?')) return;
    
    try {
      await api.delete(`/driver/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const getCategoryInfo = (category) => {
    return EXPENSE_CATEGORIES.find(c => c.value === category) || EXPENSE_CATEGORIES[7];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423] pb-24">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-white">Expenses</h1>
              <p className="text-xs text-gray-400">Track your trip expenses</p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
            onClick={() => setShowAddDialog(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="px-4 py-4">
          <Card className="border-[#3B82F6]/20 bg-gradient-to-r from-[#0D1B2A] to-[#112240]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase">Total Expenses</p>
                  <p className="text-2xl font-bold text-[#4ADE80]">
                    ${summary.total_expenses?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Records</p>
                  <p className="text-xl font-bold text-white">{summary.expense_count || 0}</p>
                </div>
              </div>
              
              {/* Category Breakdown */}
              <div className="flex flex-wrap gap-2">
                {summary.by_category?.slice(0, 4).map((cat) => {
                  const info = getCategoryInfo(cat.category);
                  const Icon = info.icon;
                  return (
                    <div key={cat.category} className="flex items-center gap-1 bg-[#0A1628] px-2 py-1 rounded text-xs">
                      <Icon className={`w-3 h-3 ${info.color}`} />
                      <span className="text-gray-400">{info.label}:</span>
                      <span className="text-white">${cat.total.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses List */}
      <div className="px-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Receipt className="w-4 h-4" />
          Recent Expenses
        </h2>

        {expenses.length === 0 ? (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="p-8 text-center">
              <Receipt className="w-12 h-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-400">No expenses recorded yet</p>
              <p className="text-xs text-gray-500 mt-1">Tap the + button to add your first expense</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {expenses.map((expense) => {
              const info = getCategoryInfo(expense.category);
              const Icon = info.icon;
              return (
                <Card key={expense.id} className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-[#112240] flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${info.color}`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{info.label}</p>
                          {expense.description && (
                            <p className="text-xs text-gray-400 truncate max-w-[150px]">{expense.description}</p>
                          )}
                          <p className="text-xs text-gray-500">{formatDate(expense.expense_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-white">${expense.amount.toFixed(2)}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDeleteExpense(expense.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {expense.location && (
                      <p className="text-xs text-gray-500 mt-2 pl-13">{expense.location}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>Record a new expense</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddExpense} className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(val) => setFormData({...formData, category: val})}
              >
                <SelectTrigger className="mt-1 bg-[#0A1628] border-[#3B82F6]/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0D1B2A] border-[#3B82F6]/20">
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className={`w-4 h-4 ${cat.color}`} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20 text-lg"
                required
              />
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="e.g., Diesel at Flying J"
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
              />
            </div>

            <div>
              <Label>Location (optional)</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="City, State"
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Expense'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverExpensesPage;
